/** 移动端 / 精简列表：库存交易记录展示 */

export function outboundObjectLabel(type) {
  const t = String(type || '').toLowerCase()
  if (t === 'customer') return '客户'
  if (t === 'processor') return '加工厂'
  if (t === 'internal') return '内部'
  if (t === 'other') return '其他'
  return type ? String(type) : '出库对象'
}

export function formatInventoryTxOutboundObject(tx, opts = {}) {
  if (!tx) return null
  const mask = !!opts.maskCounterparty
  const ob = tx.outbound_object
  if (ob?.name) {
    const name = mask ? '（已脱敏）' : ob.name
    return `${outboundObjectLabel(ob.type)}：${name}`
  }
  if (tx.outbound_object_name) {
    const role =
      tx.outbound_object_type === 'processor'
        ? '加工厂'
        : tx.outbound_object_type === 'customer'
          ? '客户'
          : '出库对象'
    if (mask) return `${role}：（已脱敏）`
    return `${role}：${tx.outbound_object_name}`
  }
  const op = tx.transaction_type?.operation_type
  if (op === 'out' || tx.transaction_type?.type_code === 'SALES_OUT') {
    return null
  }
  return null
}

export function formatInventoryTxOrderContext(tx) {
  if (!tx) return null
  const rel = String(tx.related_order_type || '').toLowerCase()
  if (rel === 'sales') {
    const parts = []
    if (tx.related_order_type_name) parts.push(`订单类型 ${tx.related_order_type_name}`)
    if (tx.delivery_order_no) parts.push(`送货单 ${tx.delivery_order_no}`)
    else if (tx.reference_number) parts.push(`参考 ${tx.reference_number}`)
    if (tx.related_order_no) parts.push(`订单 ${tx.related_order_no}`)
    return parts.length ? parts.join(' · ') : '订单出库'
  }
  if (rel === 'production') {
    if (tx.processing_number) return `加工单 ${tx.processing_number}`
    if (tx.reference_number) return `加工 ${tx.reference_number}`
    return '加工出库'
  }
  if (rel === 'purchase') {
    if (tx.reference_number) return `采购 ${tx.reference_number}`
    return '采购入库'
  }
  return null
}

export function formatInventoryTxSupplier(tx, opts = {}) {
  if (!tx) return null
  if (opts.maskCounterparty) return null
  if (tx.supplier?.supplier_name) return `供应商：${tx.supplier.supplier_name}`
  if (tx.supplier_name) return `供应商：${tx.supplier_name}`
  return null
}

export function formatInventoryTxTransfer(tx) {
  if (!tx) return null
  const tc = tx.transaction_type?.type_code
  const op = tx.transaction_type?.operation_type
  if (tc === 'TRANSFER' || op === 'transfer') {
    const fn = shortenLocationLabel(
      tx.from_location?.location_name,
      tx.from_location?.location_code
    )
    const tn = shortenLocationLabel(
      tx.to_location?.location_name,
      tx.to_location?.location_code
    )
    if (fn || tn) return `库位 ${fn || '—'} → ${tn || '—'}`
    return '库位调整'
  }
  return null
}

/** 派工完成虚拟库位等简称 */
export function shortenLocationLabel(name, code, locId) {
  const n = String(name || '').trim()
  const c = String(code || '').trim()
  if (n.includes('派工完成')) return '派工虚库'
  const cu = c.toUpperCase()
  if (cu === 'DISPATCH-COMPLETE' || (cu.includes('DISPATCH') && cu.includes('COMPLETE'))) {
    return '派工虚库'
  }
  if (n) return n
  if (c) return c
  if (locId != null && locId !== '') return `库位#${locId}`
  return '未确定位置'
}

function _fmtTxQty(v) {
  if (v === null || v === undefined || v === '') return '0'
  const n = Number(v)
  if (!Number.isFinite(n)) return '0'
  const a = Math.abs(n)
  if (Math.abs(a - Math.round(a)) < 1e-9) return String(Math.round(a))
  return String(a)
}

/** 变动数量（绝对值） */
export function formatInventoryTxChangeQty(tx) {
  return _fmtTxQty(tx?.quantity)
}

/** 前/后 + 库位（优先后端回放字段） */
export function formatInventoryTxLocationChange(tx) {
  if (!tx) return ''
  if (tx.location_change_text) return String(tx.location_change_text)
  const before = _fmtTxQty(tx.before_quantity)
  const after = _fmtTxQty(tx.after_quantity)
  const tc = tx.transaction_type?.type_code
  const op = tx.transaction_type?.operation_type
  const isTransfer = tc === 'TRANSFER' || op === 'transfer'
  if (isTransfer) {
    const fl = shortenLocationLabel(
      tx.from_location?.location_name,
      tx.from_location?.location_code,
      tx.from_location_id
    )
    const tl = shortenLocationLabel(
      tx.to_location?.location_name,
      tx.to_location?.location_code,
      tx.to_location_id
    )
    const qty = _fmtTxQty(tx.quantity)
    return `前 ${before}/${fl} → 后 ${_fmtTxQty(Math.max(0, Number(before) - Number(qty)))}/${fl}，前 0/${tl} → 后 ${qty}/${tl}`
  }
  const opIn = op === 'in'
  // 报溢/期初等常只写 location_id，to/from 为空；补录流水同此
  const loc = opIn
    ? shortenLocationLabel(
        tx.to_location?.location_name || tx.location?.location_name,
        tx.to_location?.location_code || tx.location?.location_code,
        tx.to_location_id || tx.location_id
      )
    : shortenLocationLabel(
        tx.from_location?.location_name ||
          tx.to_location?.location_name ||
          tx.location?.location_name,
        tx.from_location?.location_code ||
          tx.to_location?.location_code ||
          tx.location?.location_code,
        tx.from_location_id || tx.to_location_id || tx.location_id
      )
  return `前 ${before}/${loc} → 后 ${after}/${loc}`
}

/** 当下各库位存量（分行用数组） */
export function formatInventoryTxLocationBalances(tx) {
  const rows = tx?.location_balances_after
  if (Array.isArray(rows) && rows.length) {
    return rows.map((r) => {
      const q = r.quantity_text != null ? String(r.quantity_text) : _fmtTxQty(r.quantity)
      const lab = r.location_label || '未确定位置'
      return `${q}/${lab}`
    })
  }
  return []
}

/** 移库类重复说明（库位 A→B / 完工移库…）不再展示 */
export function shouldShowInventoryTxNotes(tx) {
  if (!tx) return false
  const notes = String(tx.notes || '').trim()
  if (!notes) return false
  const tc = tx.transaction_type?.type_code
  const op = tx.transaction_type?.operation_type
  if (tc === 'TRANSFER' || op === 'transfer') return false
  if (/^库位调整/.test(notes) || /^完工移库/.test(notes)) return false
  if (notes.includes('派工完工转入虚拟库位') && notes.includes('→')) return false
  return true
}

/** 交易对象：客户 / 供应商 / 加工厂；移库报溢等内部操作不展示 */
export function formatInventoryTxCounterparty(tx) {
  if (!tx) return null
  const notes = String(tx.notes || '')
  const salesish =
    notes.includes('送货出库') ||
    notes.includes('[delivery_oi:') ||
    tx.related_order_type === 'sales' ||
    tx.related_order_type === 'purchase' ||
    tx.related_order_type === 'production' ||
    !!(tx.outbound_object?.name || tx.outbound_object_name || tx.customer_name || tx.supplier_name)
  if (!salesish) {
    const tc = tx.transaction_type?.type_code
    const op = tx.transaction_type?.operation_type
    if (
      tc === 'TRANSFER' ||
      op === 'transfer' ||
      op === 'adjust' ||
      tx.is_adjustment ||
      tx.is_initial_stock ||
      ['OVERFLOW_IN', 'LOSS_OUT', 'CHECK_IN', 'CHECK_OUT', 'QUALITY_ADJUST', 'ADJUST'].includes(tc)
    ) {
      return null
    }
  }
  return formatInventoryTxOutboundObject(tx) || formatInventoryTxSupplier(tx) || null
}

/** 库位库存行：是否展示「已派单未开送货单」 */
export function isDispatchCompleteTransitRow(row) {
  if (!row) return false
  if (row.is_dispatch_complete_transit_row) return true
  return !!row.storage_location?.is_dispatch_complete_transit
}

export function hasDispatchPendingWithoutDelivery(row) {
  if (!row) return false
  if (isDispatchCompleteTransitRow(row)) return false
  const pending = Number(row.batch_dispatch_pending_delivery_quantity)
  return Number.isFinite(pending) && pending > 1e-9
}

/** 操作员：审批入账显示「申请：A / 审批：B」，否则仅操作人 */
export function formatInventoryTxOperator(tx) {
  if (!tx) return '-'
  if (tx.operator_display) return String(tx.operator_display)
  const a = (tx.applicant_name || tx.operator?.applicant_name || '').trim()
  const r = (tx.reviewer_name || tx.operator?.reviewer_name || '').trim()
  if (a && r) return `申请：${a} / 审批：${r}`
  return (
    tx.operator?.full_name ||
    tx.operator?.username ||
    tx.operator_name ||
    tx.created_by ||
    '-'
  )
}
